// Atlas Standard — design tokens (US-874 · R-2, R-3).
//
// Single source of truth for the institutional design system. Values are ported
// verbatim from the approved client mockup
//   inputs/Connectivity Mapping Tool - Standalone.html  (:root CSS variables)
// verified 2026-06-24. Feature US must read colors/spacing/dimensions from here
// (directly or via the MUI theme in ../themes.js) — no ad-hoc inline values.

// Institutional palette ------------------------------------------------------
export const colors = {
  // Brand
  navy: "#002569", // --navy
  navyDeep: "#004d99", // --navy-deep
  navyTint: "#e6ebf3", // --navy-tint
  // Accent
  gold: "#FBB00E", // --gold
  // Surfaces
  canvas: "#f8f9fa", // --canvas
  white: "#ffffff", // --white
  mapSurface: "#eceff2", // map placeholder surface (mockup .map-wrap)
  // Text
  textDarkest: "#212529", // --text-darkest
  textPrimary: "#343a40", // --text-primary
  textSecondary: "#495057", // --text-secondary
  annotation: "#6c757d", // --annotation
  // Borders
  borderLight: "#dee2e6", // --border-light
  borderMedium: "#ced4da", // --border-medium
  neutralMid: "#adb5bd", // --neutral-mid
};

// Connectivity layer palette — reference for US-875+ / EPIC-16. NOT applied to
// the map in US-874; exposed here only so feature US share one source.
export const layerColors = {
  fourG: "#2c5fa8", // --layer-4g
  backbone: "#5a3d8a", // --layer-backbone
  fiber: "#d4a017", // --layer-fiber
  satellite: "#b8323a", // --layer-satellite
  served: "#4a8c3a", // --layer-served
  wifi: "#007a8c", // --layer-wifi
};

// Sequential ramps for the continuous raster gap layers (US-920 · R-8) -------
// Light -> dark = fewer -> more uncovered people per 1 km cell. Value 0 renders
// TRANSPARENT (a cell with no gap paints nothing), so the light end carries
// the first visible severity step. Designed with the dataviz method and
// VALIDATED computationally (ordinal ramp checks: monotone lightness,
// dL >= 0.06 per step, light end >= 2:1 contrast on the map surface #eceff2;
// amber<->blue overlap pair CVD dE 93 under protan/deutan - colorblind-safe).
// Hue anchors: layerColors.fiber (amber) and layerColors.fourG (blue).
// A-8 (Lead UX): each step also carries a GRADUATED ALPHA (0.55 -> 0.95) so
// severity is encoded in opacity as well as lightness - low-severity areas
// (most of the painted surface) let the basemap show through for
// orientation, while hotspots stay near-solid. Hues unchanged from the
// validated ramp above; the effective on-screen opacity is this alpha times
// the layer's zoom-graduated raster-opacity (layerConfig).
export const rasterRamps = {
  fixedGap: [
    "rgba(212, 160, 23, 0.55)",
    "rgba(168, 126, 15, 0.7)",
    "rgba(122, 90, 8, 0.85)",
    "rgba(79, 58, 4, 0.95)",
  ],
  mobileGap: [
    "rgba(133, 169, 216, 0.55)",
    "rgba(85, 128, 189, 0.7)",
    "rgba(44, 95, 168, 0.85)",
    "rgba(28, 61, 110, 0.95)",
  ],
};

// Typography stacks ----------------------------------------------------------
export const fonts = {
  sans: "'Public Sans', 'Ideal Sans', -apple-system, Arial, sans-serif", // --sans
  serif: "'Source Serif 4', 'Mercury Text G1', Georgia, serif", // --serif
};

// Spacing scale (rem) — --sp-xs … --sp-xl.
export const spacing = {
  xs: "0.25rem", // --sp-xs
  sm: "0.5rem", // --sp-sm
  md: "1rem", // --sp-md
  lg: "1.5rem", // --sp-lg
  xl: "2.5rem", // --sp-xl
};

// Layout dimensions (px) -----------------------------------------------------
// Mockup grid is 3 columns (rail · panel · map); the map is full-width (1fr) and
// there is NO right panel (corrected 2026-06-25, see US-874 spec amendment).
export const layout = {
  bannerH: 4, // ADB rainbow brand stripe (mockup .rainbow, 3px → 4px banner row)
  headerH: 56, // --header-h
  railW: 72, // --rail-w
  panelW: 420, // --panel-w (left tabbed panel; map takes the remaining 1fr)
};

// Elevation ------------------------------------------------------------------
export const shadows = {
  card: "0 8px 24px -8px rgba(0, 37, 105, 0.18), 0 2px 6px -2px rgba(0,0,0,0.06)", // --shadow-card
  anchor: "0 1px 3px 0 rgba(0,0,0,0.08), 0 1px 2px 0 rgba(0,0,0,0.05)", // --shadow-anchor
};

// ADB brand rainbow — six equal bands, left→right (banner, US-874 · R-4).
export const rainbow = [
  "#d83333",
  "#f29638",
  "#f0c419",
  "#5d9c3b",
  "#2c8aa5",
  "#6b4694",
];

const tokens = {
  colors,
  layerColors,
  rasterRamps,
  fonts,
  spacing,
  layout,
  shadows,
  rainbow,
};

export default tokens;
